import actionTypes from '../actions/reduxActions/actionTypes';
import Model from '../utils/model';
import { assocPath, concat, difference, merge } from 'ramda';

/**
 *
 * @param page the page metadata
 * @param alteredContainer metadata describing the container that has been dropped
 * @param containerId the id of the container that has been dropped
 * @param parent the id of the parent container of the container that has been dropped
 * @param order the new sort order value of the page being dropped
 * @param isCurrent flag to determine whether the containers current siblings are to be reordered
 * @param isRemoving flag to determine if the container is actually being deleted
 *
 * @description Will reset the sort order of page container siblings and
 * return as an array of page containers. This will account for reordering siblings
 * if the page containers parent is changed
 */
export const reOrderPageContainers = ({
    page,
    alteredContainer,
    containerId,
    parent,
    order,
    isCurrent,
    isRemoving = false,
}) => {

    const parentId = !isCurrent ? parent : alteredContainer.parentId;

    const pageContainerSiblings = page.pageContainers.filter(
        container => container.parentId === parentId && container.id !== containerId,
    );

    // We only want to insert the altered page container if the page container
    // is just being reordered within it's current parent container
    // or if the container is being moved into a new parent
    if (!isCurrent || (isCurrent && !parent)) {
        pageContainerSiblings.splice(
            parseInt(order, 10),
            0,
            alteredContainer,
        );
    }

    const reOrdered = pageContainerSiblings.map((container, index) => {
        const changes = {
            parentId,
            order: index,
        };
        return merge(container, changes);
    });

    const mergedPageContainers = assocPath(
        ['pageContainers'],
        concat(
            reOrdered,
            page.pageContainers.filter(
                container => container.parentId !== parentId,
            ),
        ),
        page,
    );

    // Page container has just been dropped within its current parent
    // or has been added to or removed from the page canvas
    if ((!parent && isCurrent) || (parent && !isCurrent) || isRemoving) {
        return mergedPageContainers;
    }

    // The container has been dropped from an existing parent into a new parent
    return reOrderPageContainers({
        page: mergedPageContainers,
        alteredContainer,
        containerId,
        parent,
        order,
        isCurrent: false,
    });
};

/**
 *
 * @param page the page metadata
 * @param parent the parent ID
 *
 * @description Will return an array of page containers which
 * are all offspring of the original parent
 */
const getContainerOffspring = (page, parent) => {

    const containersToRemove = [];
    page.pageContainers.forEach((pageContainer) => {
        if (pageContainer.parentId === parent) {
            const children = getContainerOffspring(
                page,
                pageContainer.id,
            );
            if (children.length) {
                children.forEach((child) => {
                    containersToRemove.push(child);
                });
            }
            containersToRemove.push(pageContainer);
        }
    });
    return containersToRemove;
};

const onPageContainerDrop = ({ page, containerId, parent, order, type }) => {

    const id = containerId || Model.guid();

    // If there is no container ID then we are adding a new container
    const pageContainerDropped = containerId ? page.pageContainers.find(
        container => container.id === containerId,
    ) : {
        label: null,
        order,
        parentId: parent,
        attributes: null,
        containerType: type,
        developerName: 'New Container',
        id,
        tags: null,
    };

    const orderedContainers = reOrderPageContainers({
        page,
        alteredContainer: pageContainerDropped,
        containerId,
        parent,
        order,
        isCurrent: true,
    });

    // We need to set the page container that has been moved
    // to be the active container that is in focus
    const containersWithActiveContainerSet = orderedContainers.pageContainers.map((container) => {
        if (container.id === id) {
            return assocPath(
                ['isActive'],
                true,
                container,
            );
        }
        return assocPath(
            ['isActive'],
            false,
            container,
        );
    });

    return assocPath(
        ['pageContainers'],
        containersWithActiveContainerSet,
        orderedContainers,
    );
};

export const removePageContainer = ({ page, containerId }) => {

    const pageContainerToRemove = page.pageContainers.find(
        container => container.id === containerId,
    );

    const offspringToRemove = getContainerOffspring(
        page,
        pageContainerToRemove.id,
    );

    const containersWithSpecificOffspringRemoved = difference(
        page.pageContainers,
        offspringToRemove,
    );

    const pageMetadataWithSpecificOffspringRemoved = assocPath(
        ['pageContainers'],
        containersWithSpecificOffspringRemoved,
        page,
    );

    return reOrderPageContainers({
        page: pageMetadataWithSpecificOffspringRemoved,
        alteredContainer: pageContainerToRemove,
        containerId,
        parent: pageContainerToRemove.parentId,
        order: pageContainerToRemove.order,
        isCurrent: true,
        isRemoving: true,
    });
};

export const setActivePageContainer = ({ page, containerId }) => {

    const setActiveContainer = page.pageContainers.map((container) => {
        if (container.id === containerId) {
            return assocPath(
                ['isActive'],
                true,
                container,
            );
        }
        return assocPath(
            ['isActive'],
            false,
            container,
        );
    });

    return assocPath(
        ['pageContainers'],
        setActiveContainer,
        page,
    );
};

const pageEditor = (page = null, { type, payload, error, meta }) => {

    switch (type) {
    case actionTypes.PAGE_SET:
        return payload;
    case actionTypes.PAGE_CONTAINER_REMOVE:
        return removePageContainer({ page, ...payload });
    case actionTypes.PAGE_CONTAINER_DROP:
        return onPageContainerDrop({ page, ...payload });
    case actionTypes.PAGE_CONTAINER_SET_ACTIVE:
        return setActivePageContainer({ page, ...payload });
    default:
        return page;
    }
};

export default pageEditor;
